import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'

/** Project languages and string translations — mirrors stacks-agents `src/app/api/translations/`. */
@Injectable({ providedIn: 'root' })
export class TranslationsApiService {
  constructor(private http: HttpClient) {}

  /** Builder JWT for the stacks-agents API */
  private agentsAuthHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${localStorage.getItem('token')}` })
  }

  getTranslationConfig(projectId: string): Promise<{ defaultLanguage: string; translateNumbers: boolean } | null> {
    return this.http
      .get<{ data?: { defaultLanguage: string; translateNumbers: boolean } }>(`${environment.agentsUrl}v4/storefront/translations/config`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? null),
        catchError(() => of(null)),
      )
      .toPromise() as Promise<{ defaultLanguage: string; translateNumbers: boolean } | null>
  }

  getProjectLanguages(projectId: string): Promise<string[]> {
    return this.http
      .get<{ data?: string[] }>(`${environment.agentsUrl}v4/storefront/translations/languages`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId },
      })
      .pipe(
        map(body => body?.data ?? []),
        catchError(() => of([])),
      )
      .toPromise() as Promise<string[]>
  }

  getAllTranslationsForLanguage(projectId: string, language: string): Promise<Record<string, { value: string }>> {
    return this.http
      .get<{ data?: Record<string, { value: string }> }>(`${environment.agentsUrl}v4/storefront/translations`, {
        headers: this.agentsAuthHeaders(),
        params: { projectId, language },
      })
      .pipe(
        map(body => body?.data ?? {}),
        catchError(() => of({})),
      )
      .toPromise() as Promise<Record<string, { value: string }>>
  }

  /** Upserts the translation config for the project. */
  async upsertTranslationConfig(projectId: string, config: { defaultLanguage: string; translateNumbers: boolean }): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/translations/config`,
        { projectId, config },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Adds a language to the project (duplicates ignored server-side). */
  async addProjectLanguage(projectId: string, language: string): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/translations/languages`,
        { projectId, language },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Bulk-upserts translations for a language. */
  async upsertTranslations(projectId: string, language: string, translations: Record<string, string>): Promise<void> {
    await this.http
      .post(`${environment.agentsUrl}v4/creation/translations`,
        { projectId, language, translations },
        { headers: this.agentsAuthHeaders() })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }

  /** Hard-deletes a project language. */
  async deleteProjectLanguage(projectId: string, language: string): Promise<void> {
    await this.http
      .request('DELETE', `${environment.agentsUrl}v4/deletion/languages`, {
        headers: this.agentsAuthHeaders(),
        body: { projectId, language },
      })
      .pipe(catchError(() => of(null)))
      .toPromise()
  }
}
